// utils/apiClient.ts
import { getCookie, setCookie, deleteCookie } from './cookies';
import { authService } from './authService';

export const ensureValidToken = async () => {
  const token = getCookie('token');
  const refreshToken = getCookie('refresh_token');
  const tokenExpiry = getCookie('token_expiry');

  if (!token || !refreshToken || !tokenExpiry) {
    // If any authentication artifacts are missing, force a logout
    throw new Error('No authentication token available');
  }

  const expiryTime = Number(tokenExpiry);
  const timeUntilExpiry = expiryTime - Date.now();
  
  console.log(`Token time until expiry: ${Math.round(timeUntilExpiry / 1000)}s`);

  // Proactively refresh if the token is expiring in the next 60 seconds
  if (timeUntilExpiry < 60 * 1000) {
    try {
      console.log('Token is expiring soon. Attempting to refresh proactively...');
      const response = await authService.refreshToken(refreshToken);
      console.log('Token refreshed successfully.');
      setCookie('token', response.access_token, response.expires_in);
      setCookie('refresh_token', response.refresh_token, response.expires_in * 2);
      setCookie('token_expiry', String(Date.now() + response.expires_in * 1000), response.expires_in);
      return response.access_token;
    } catch (error) {
      console.error('Proactive token refresh failed:', error);
      // If refresh fails, clear cookies and redirect to login
      deleteCookie('token');
      deleteCookie('refresh_token');
      deleteCookie('token_expiry');
      deleteCookie('username');
      deleteCookie('user_id');
      window.location.href = '/?callbackUrl=' + encodeURIComponent(window.location.pathname);
      throw new Error('Authentication failed');
    }
  } else {
    console.log('Token is still valid. No refresh needed.');
  }

  return token;
};


export async function fetchWithAutoRefresh(url: string, options: RequestInit = {}) {
  const makeRequest = async (token: string) => {
    return fetch(url, {
      ...options,
      headers: {
        ...options.headers,
        'Authorization': `Bearer ${token}`,
      },
    });
  };

  const token = await ensureValidToken();
  let response = await makeRequest(token);
  // If the request fails due to token expiration
  if (response.status === 401) {
    const refreshToken = getCookie('refresh_token');
    if (refreshToken) {
      try {
        // Attempt to refresh the token
        await authService.refreshToken(refreshToken);
        
        // Get the new token
        const newToken = getCookie('token');
        if (newToken) {
          // Retry the original request with the new token
          response = await makeRequest(newToken);
        }
      } catch (error) {
        console.error('Token refresh failed:', error);
        // Handle refresh failure (e.g., redirect to login)
        window.location.href = '/?callbackUrl=' + encodeURIComponent(window.location.pathname);
        throw new Error('Authentication failed');
      }
    }
  }

  if (!response.ok) {
    try {
      const copy = response.clone();
      const txt = await copy.text();
      console.error(`HTTP ${response.status} ${response.statusText} for ${url}:`, txt?.slice(0, 2000));
    } catch {}
  }
  return response;
}

export const createSession = async (name: string, created_by: string) => {
  const response = await fetchWithAutoRefresh(`/api/sessions/`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ name, created_by }),
  });
  return response.json();
};

export const saveRoleSheetData = async (sessionId: string, data: any) => {
  const response = await fetchWithAutoRefresh(`/api/sessions/${sessionId}/save-rolesheet-data`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });
  return response.json();
};

export const runSimulation = async (sessionId: string, num_runs: number = 1) => {
  const response = await fetchWithAutoRefresh(`/api/sessions/${sessionId}/run-simulation`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ num_runs }),
  });
  if (!response.ok) {
    throw new Error('Failed to run simulation');
  }
  return response.json();
};

export const cancelSimulation = async (sessionId: string) => {
  const response = await fetchWithAutoRefresh(`/api/sessions/${sessionId}/cancel-simulation`, {
    method: 'POST',
  });
  if (!response.ok) {
    throw new Error('Failed to cancel simulation');
  }
  return response.json();
};

type SessionStatus = {
  status: 'idle' | 'starting' | 'running' | 'completed' | 'failed';
  completed_runs: number;
  total_runs: number;
};

export const getSimulationStatus = async (sessionId: string): Promise<SessionStatus> => {
  const response = await fetchWithAutoRefresh(
    `/api/sessions/${sessionId}/status`,
    { cache: 'no-store' } // important for polling
  );

  if (!response.ok) {
    throw new Error(`Failed to get simulation status: ${response.status} ${response.statusText}`);
  }

  // If your route ever returns 204 (no content), guard it:
  if (response.status === 204) {
    return { status: 'idle', completed_runs: 0, total_runs: 0 };
  }

  const data = await response.json();
  console.log("Simulation status fetched successfully", data); // <-- log the JSON, not Response
  return data;
};

export const getSimulationPlot = async (sessionId: string, runId: string, plotType: string) => {
  const response = await fetchWithAutoRefresh(`/api/sessions/${sessionId}/plots/${runId}/${plotType}`);
  if (!response.ok) {
    throw new Error('Failed to get simulation plot');
  }
  return response.json();
};

export const getExcelAllPlots = async (sessionId: string) => {
  const response = await fetchWithAutoRefresh(`/api/sessions/${sessionId}/plots/excel/all`);
  if (!response.ok) {
    throw new Error('Failed to get all excel plots');
  }
  return response.json();
};

export const getExcelDefaultPlots = async (sessionId: string) => {
  const response = await fetchWithAutoRefresh(`/api/sessions/${sessionId}/plots/excel/default`);
  if (!response.ok) {
    throw new Error('Failed to get excel default plots');
  }
  return response.json();
};

export const getExcelIndividualIssuePlots = async (sessionId: string) => {
  const response = await fetchWithAutoRefresh(`/api/sessions/${sessionId}/plots/excel/individual_issues`);
  if (!response.ok) {
    throw new Error('Failed to get excel individual issue plots');
  }
  return response.json();
};

export const getExcelStakeholderPreferencesPlots = async (sessionId: string) => {
  const response = await fetch(`/api/sessions/${sessionId}/plots/excel/stakeholder_preferences`);
  if (!response.ok) {
    throw new Error('Failed to fetch stakeholder preferences plots');
  }
  return response.json();
};

export const getNegotiationFlowPlots = async (sessionId: string, runId: string) => {
  const response = await fetch(`/api/sessions/${sessionId}/plots/excel/negotiation_flow/${runId}`);
  if (!response.ok) {
    throw new Error('Failed to fetch negotiation flow plots');
  }
  return response.json();
};

export const getSimulationRuns = async (sessionId: string) => {
  const response = await fetchWithAutoRefresh(`/api/sessions/${sessionId}/simulation/runs`);
  if (!response.ok) {
    throw new Error('Failed to get simulation runs');
  }
  return response.json();
};

export const getSimulationResults = async (sessionId: string) => {
  const response = await fetchWithAutoRefresh(`/api/sessions/${sessionId}/simulation/results`);
  if (!response.ok) {
    throw new Error('Failed to get simulation results');
  }
  return response.json();
};

export const getSimulationThoughts = async (sessionId: string, runId: string) => {
  const response = await fetchWithAutoRefresh(`/api/sessions/${sessionId}/simulation/thoughts/${runId}`);
  if (!response.ok) {
    throw new Error('Failed to get simulation thoughts');
  }
  return response.json();
};

export const uploadDocument = async (sessionId: string, file: File) => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await fetchWithAutoRefresh(`/api/sessions/${sessionId}/documents`, {
    method: 'POST',
    body: formData,
  });
  return response.json();
};

// Moderator-uploaded local simulation results (for comparing against the
// backend-generated plots). See uploaded_results.py on the backend.
export const uploadResultSet = async (sessionId: string, file: File, label?: string) => {
  const formData = new FormData();
  formData.append('file', file);
  if (label) {
    formData.append('label', label);
  }
  const response = await fetchWithAutoRefresh(`/api/sessions/${sessionId}/uploaded-results`, {
    method: 'POST',
    body: formData,
  });
  if (!response.ok) {
    const errorBody = await response.text().catch(() => '');
    let detail = errorBody;
    try {
      detail = JSON.parse(errorBody).detail || errorBody;
    } catch {
      // errorBody wasn't JSON; use it as-is
    }
    throw new Error(detail || 'Failed to upload result set');
  }
  return response.json();
};

export const getUploadedResultSets = async (sessionId: string) => {
  const response = await fetchWithAutoRefresh(`/api/sessions/${sessionId}/uploaded-results`);
  if (!response.ok) {
    throw new Error('Failed to get uploaded result sets');
  }
  return response.json();
};

export const getUploadedResultDefaultPlots = async (sessionId: string, uploadId: string) => {
  const response = await fetchWithAutoRefresh(`/api/sessions/${sessionId}/uploaded-results/${uploadId}/plots/default`);
  if (!response.ok) {
    throw new Error('Failed to get uploaded result default plots');
  }
  return response.json();
};

export const getUploadedResultIndividualIssuePlots = async (sessionId: string, uploadId: string) => {
  const response = await fetchWithAutoRefresh(`/api/sessions/${sessionId}/uploaded-results/${uploadId}/plots/individual_issues`);
  if (!response.ok) {
    throw new Error('Failed to get uploaded result individual issue plots');
  }
  return response.json();
};

export const deleteUploadedResultSet = async (sessionId: string, uploadId: string) => {
  const response = await fetchWithAutoRefresh(`/api/sessions/${sessionId}/uploaded-results/${uploadId}`, {
    method: 'DELETE',
  });
  if (!response.ok) {
    throw new Error('Failed to delete uploaded result set');
  }
  return response.json();
};

export const getDocuments = async (sessionId: string) => {
  const response = await fetchWithAutoRefresh(`/api/sessions/${sessionId}/documents`);
  return response.json();
};

export const deleteDocument = async (documentId: string) => {
  const response = await fetchWithAutoRefresh(`/api/documents/${documentId}`, {
    method: 'DELETE',
  });
  return response.json();
};

export const analyzeDocument = async (documentId: string) => {
  const response = await fetchWithAutoRefresh(`/api/documents/${documentId}/analyze`, {
    method: 'POST',
  });
  return response.json();
};

export const elaborateTopic = async (sessionId: string) => {
  const response = await fetchWithAutoRefresh(`/api/wizard/sessions/${sessionId}/elaborate-topic`, {
    method: 'POST',
  });
  return response.json();
};

export const generateStakeholder = async (sessionId: string) => {
  const response = await fetchWithAutoRefresh(`/api/wizard/sessions/${sessionId}/generate-stakeholder`, {
    method: 'POST',
  });
  return response.json();
};

export const generateSummary = (sessionId: string, onData: (data: string) => void) => {
  return new Promise(async (resolve, reject) => {
    try {
      const response = await fetchWithAutoRefresh(`/api/wizard/sessions/${sessionId}/generate-summary`, {
        method: 'POST',
      });

      if (!response.body) {
        return reject(new Error("Response body is null"));
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();

      const read = () => {
        reader.read().then(({ done, value }) => {
          if (done) {
            resolve(undefined);
            return;
          }
          const chunk = decoder.decode(value, { stream: true });
          onData(chunk);
          read();
        }).catch(reject);
      };

      read();
    } catch (error) {
      reject(error);
    }
  });
};

export const saveConversation = async (sessionId: string, conversation: any[]) => {
  const response = await fetchWithAutoRefresh(`/api/sessions/${sessionId}/save_conversation`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(conversation),
  });
  return response.json();
};

export const getConversation = async (sessionId: string) => {
  const response = await fetchWithAutoRefresh(`/api/sessions/${sessionId}/conversation`);
  return response.json();
};

export const clearConversation = async (sessionId: string) => {
  const response = await fetchWithAutoRefresh(`/api/sessions/${sessionId}/conversation`, {
    method: 'DELETE',
  });
  
  if (!response.ok) {
    let errorMessage = `HTTP ${response.status} ${response.statusText}`;
    try {
      const errorData = await response.json();
      errorMessage = errorData.detail || errorMessage;
    } catch {
      // If JSON parsing fails, use the default error message
    }
    throw new Error(`Failed to clear conversation: ${errorMessage}`);
  }
  
  // Try to parse JSON response, but don't fail if it's empty
  try {
    const data = await response.json();
    return data.ok || true;
  } catch {
    // If no JSON body (e.g., 204 response), consider it successful
    return true;
  }
};

export const getSummaryAndSuggestions = async (sessionId: string) => {
  const response = await fetchWithAutoRefresh(`/api/wizard/sessions/${sessionId}/generate-summary-and-suggestions`, {
    method: 'POST',
  });
  return response.json();
};

export const getSummaryStatus = async (sessionId: string) => {
  const response = await fetchWithAutoRefresh(`/api/wizard/sessions/${sessionId}/summary-status`);
  return response.json();
};

export const generateInitialSummary = async (sessionId: string) => {
  const response = await fetchWithAutoRefresh(`/api/wizard/sessions/${sessionId}/generate-initial-summary`, {
    method: 'POST',
  });
  return response.json();
};

export const generateAlternatives = async (sessionId: string) => {
  const response = await fetchWithAutoRefresh(`/api/wizard/sessions/${sessionId}/generate-alternatives`, {
    method: 'POST',
  });
  return response.json();
};

export const regenerateSummaryWithVotes = async (sessionId: string, votes: any) => {
  const response = await fetchWithAutoRefresh(`/api/sessions/${sessionId}/regenerate-voting-summary`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(votes),
  });
  return response.json();
};

export const regenerateItem = async (
  sessionId: string,
  itemType: 'issue' | 'option',
  issueCode: string,
  currentContent: { issue?: string; description?: string; label?: string; options?: any[] },
  optionCode?: string,
  feedback?: string
) => {
  const response = await fetchWithAutoRefresh(`/api/sessions/${sessionId}/regenerate-item`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      item_type: itemType,
      issue_code: issueCode,
      option_code: optionCode,
      current_content: currentContent,
      feedback: feedback,
    }),
  });
  return response.json();
};

export const getGroups = async (sessionId: string) => {
  const response = await fetchWithAutoRefresh(`/api/sessions/${sessionId}/groups`);
  return response.json();
};

export const addGroup = async (sessionId: string, name: string) => {
  const response = await fetchWithAutoRefresh(`/api/sessions/${sessionId}/groups`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ name }),
  });
  return response.json();
};

export const joinSession = async (sessionId: string, userId: string, role: string, username: string, partyId?: string, hasVeto?: boolean) => {
  const response = await fetchWithAutoRefresh(`/api/sessions/${sessionId}/participants`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ user_id: userId, role: role, username: username, party_id: partyId, has_veto: hasVeto }),
  });
  return response.json();
};

export const deleteParticipant = async (sessionId: string, userId: string) => {
  const response = await fetchWithAutoRefresh(`/api/sessions/${sessionId}/participants/${userId}`, {
    method: 'DELETE',
  });
  // Success is indicated by 204; treat 404 as no-op (not present)
  if (response.status === 204 || response.status === 404) return true;
  if (!response.ok) {
    let body: any = null;
    try { body = await response.json(); } catch {}
    throw new Error(`Failed to delete participant: ${body?.detail || response.statusText}`);
  }
  return true;
};

export const getParticipants = async (sessionId: string) => {
    const response = await fetchWithAutoRefresh(`/api/sessions/${sessionId}/participants`);
    return response.json();
};

export const submitWizardStep = async (participantId: string, stepNum: number, data: any) => {
  const response = await fetchWithAutoRefresh(`/api/wizard/${participantId}/step/${stepNum}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });
  return response.json();
};

export const getWizardInputs = async (participantId: string) => {
  const response = await fetchWithAutoRefresh(`/api/wizard/${participantId}/inputs`);
  return response.json();
};

export const getUsers = async () => {
    const response = await fetchWithAutoRefresh(`/api/auth/admin/users`);
    if (!response.ok) {
        throw new Error('Failed to fetch users');
    }
    return response.json();
};

export const getUserRoles = async (userId: string) => {
    const response = await fetchWithAutoRefresh(`/api/auth/admin/users/${userId}/role-mappings`);
    if (!response.ok) {
        throw new Error('Failed to fetch user roles');
    }
    const data = await response.json();
    return data.realmMappings || [];
};

export const getUserGroups = async (userId: string) => {
    const response = await fetchWithAutoRefresh(`/api/auth/admin/users/${userId}/groups`);
    if (!response.ok) {
        throw new Error('Failed to fetch user groups');
    }
    return response.json();
};

export const getSessions = async () => {
  console.log("Attempting to fetch /api/sessions from the frontend");
  try {
    const response = await fetchWithAutoRefresh(`/api/sessions`);
    console.log("Response from /api/sessions:", response.status, response.statusText);
    if (!response.ok) {
      const errorBody = await response.text();
      console.error("Failed to fetch sessions. Status:", response.status, "Body:", errorBody);
      throw new Error(`Failed to fetch sessions: ${response.statusText}`);
    }
    return response.json();
  } catch (error) {
    console.error("An error occurred while fetching sessions:", error);
    throw error;
  }
};

export async function getSessionSummary(id: string) {
  console.log("Fetching session summary for ID:", id);
  const res = await fetch(`/api/sessions/${id}/summary`, { cache: 'no-store' });

  if (!res.ok) {
    let detail: any = null;
    try {
      detail = await res.json();
    } catch {
      try { detail = await res.text(); } catch { /* ignore */ }
    }

    const message =
      (detail && (detail.error || detail.message || JSON.stringify(detail))) ||
      `HTTP ${res.status}`;
    throw new Error(`Failed to fetch session summary: ${message}`);
  }

  return res.json();
}



export const getSession = async (sessionId: string) => {
  const response = await fetchWithAutoRefresh(`/api/sessions/${sessionId}`);
  return response.json();
};

export const deleteSession = async (sessionId: string) => {
  const response = await fetch(`/api/sessions?id=${sessionId}`, {
    method: 'DELETE',
  });
  return response;
};

export const getParties = async (sessionId: string) => {
  const response = await fetchWithAutoRefresh(`/api/sessions/${sessionId}/parties`);
  return response.json();
};

export const getPromptFields = async (sessionId: string) => {
  const response = await fetchWithAutoRefresh(`/api/sessions/${sessionId}/prompt-fields`);
  return response.json();
};

export const deletePromptField = async (sessionId: string, fieldName: string) => {
  const response = await fetchWithAutoRefresh(`/api/sessions/${sessionId}/prompt-fields/${fieldName}`, {
    method: 'DELETE',
  });
  return response.json();
};

export const updateSession = async (sessionId: string, data: { name?: string; status?: 'Draft' | 'Live', rules?: string, goal?: string, summary?: string, suggestions?: string[] }) => {
  const response = await fetchWithAutoRefresh(`/api/sessions/${sessionId}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });
  return response.json();
};

export const defineParties = async (sessionId: string, data: { broad_topic: string, parties: { name: string, id: string, session_id: string }[], prompt_fields: { name: string, is_deletable: boolean }[] }) => {
  const response = await fetchWithAutoRefresh(`/api/wizard/sessions/${sessionId}/define-parties`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });
  return response.json();
};

export const configurePartyDetails = async (sessionId: string, partyId: string, data: any) => {
  const response = await fetchWithAutoRefresh(`/api/wizard/sessions/${sessionId}/configure-party-details`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      party_id: partyId,
      ...data,
    }),
  });
  return response.json();
};

export const generateField = async (
  sessionId: string,
  partyName: string,
  fieldToGenerate: string,
  description: string,
  modelSettings: any
) => {
  const response = await fetchWithAutoRefresh(
    `/api/wizard/sessions/generate-field`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        session_id: sessionId,
        party_name: partyName,
        field_to_generate: fieldToGenerate,
        description: description,
        model_settings: modelSettings,
      }),
    }
  );
  if (!response.ok) {
    return "";
  }
  return response.text();
};

export const getSystemMessages = async (sessionId: string) => {
  const response = await fetchWithAutoRefresh(`/api/wizard/sessions/${sessionId}/generate-system-messages`);
  return response.json();
};

export const getSpecifiedTopic = async (sessionId: string) => {
  const response = await fetchWithAutoRefresh(`/api/wizard/sessions/${sessionId}/generate-specified-topic`);
  return response.json();
};

export const startConversation = (sessionId: string, onMessage: (data: any) => void, customTopic?: string) => {
  return new Promise(async (resolve, reject) => {
    try {
      const response = await fetchWithAutoRefresh(`/api/sessions/${sessionId}/conversation`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: customTopic ? JSON.stringify({ custom_topic: customTopic }) : undefined,
      });

      if (!response.body) {
        return reject(new Error("Response body is null"));
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();

      const read = () => {
        reader.read().then(({ done, value }) => {
          if (done) {
            resolve(undefined);
            return;
          }

          const chunk = decoder.decode(value, { stream: true });
          const messages = chunk.split('\n\n').filter(Boolean);

          messages.forEach(message => {
            if (message.startsWith('data: ')) {
              try {
                const json = JSON.parse(message.substring(6));
                onMessage(json);
              } catch (error) {
                console.error('Error parsing stream message', error);
              }
            }
          });

          read();
        }).catch(reject);
      };

      read();
    } catch (error) {
      reject(error);
    }
  });
};

export const getAllFinalWorkspaces = async (sessionId: string) => {
  const response = await fetchWithAutoRefresh(`/api/sessions/${sessionId}/all_workspaces`);
  if (!response.ok) {
    throw new Error("Failed to fetch all final workspaces");
  }
  return response.json();
};

export const getFinalWorkspace = async (id: string, role: string): Promise<any> => {
  const response = await fetchWithAutoRefresh(`/api/sessions/${id}/workspace?role=${role}`);
  return response.json();
};

export const saveScore = async (
  sessionId: string,
  scoreData: {
    issue_code: string;
    option_code?: string;
    score: number;
    max_score?: number;
    score_type?: string;
  }
) => {
  const response = await fetchWithAutoRefresh(
    `/api/sessions/${sessionId}/scores`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(scoreData),
    }
  );
  return response.json();
};

export const getScores = async (sessionId: string) => {
  const response = await fetchWithAutoRefresh(
    `/api/sessions/${sessionId}/scores`
  );
  return response.json();
};

// Moderator-only: downloads the current stakeholder preference scores as a CSV
// file (same "Stakeholder;A1;A2;..." format as the pre-session preference
// sheets) and saves it via the browser's normal file-download flow. Reads
// live from the backend, which is updated on every stakeholder score edit, so
// this works while stakeholders are still filling out the form.
export const downloadScores = async (sessionId: string) => {
  const response = await fetchWithAutoRefresh(`/api/sessions/${sessionId}/scores/export`);

  if (!response.ok) {
    let detail = '';
    try { detail = await response.text(); } catch {}
    throw new Error(detail || `Failed to export scores (${response.status})`);
  }

  const blob = await response.blob();
  const disposition = response.headers.get('Content-Disposition') || '';
  const filenameMatch = disposition.match(/filename="?([^"]+)"?/);
  const filename = filenameMatch ? filenameMatch[1] : `scores_${sessionId}.csv`;

  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

export const generateRoleSheet = async (data: any) => {
  const response = await fetchWithAutoRefresh('/api/rolesheet', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });
  return response.json();
};

export const generateInitialDeal = async (datasets: any) => {
  const response = await fetchWithAutoRefresh('/api/initial-deal', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ datasets }),
  });
  return response.json();
};

export const generateInitialDealParty = async (dataset: any) => {
  const response = await fetchWithAutoRefresh('/api/initial-deal-party', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ dataset }),
  });
  return response.json();
};

export const getVotes = async () => {
  const response = await fetchWithAutoRefresh('/api/votes');
  if (!response.ok) {
    throw new Error('Failed to fetch votes');
  }
  return response.json();
};

export const submitVotes = async (sessionId: string, votes: any) => {
  const response = await fetchWithAutoRefresh(`/api/sessions/${sessionId}/votes`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(votes),
  });
  return response.json();
};
